var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { getConfig } from './configs';
import { ProjectsRepository } from './projects/Repository';
import { SessionManager } from './tracking/sessions';
import { RemotePackageRepository } from './workflows/Steps/Repository';
import { PackageMemoryCache } from './workflows/Steps/Repository/PackageMemoryCache';
import { Context } from './workflows/Context/Context';
import { WorkflowManager } from './workflows/WorkflowManager/WorkflowManager';
import { VERSION } from './constants';
import { EventLogger } from './tracking/EventLogger';
/**
 * Samelogic Client SDK API. The Samelogic class handles the creation of a client instance.
 *
 * @public
 */
var Samelogic = /** @class */ (function () {
    /**
     * Samelogic Client SDK API - instance constructor. The Samelogic class handles the creation and configuration
     * of a client instance.
     *
     * @param options - the configuration options for the client instance.
     *
     * @public
     */
    function Samelogic(options) {
        this.options = options;
        /**
         * The current version of the library
         */
        this.version = VERSION;
        //#endregion
        this.workflowManagers = new Map();
        this.config = getConfig(options);
        // Temporary bind this to console for now
        this.logger = console;
        this.sessionManager = new SessionManager(this.options.envId, options.user);
        this.projectsRepository = new ProjectsRepository(this.config, this.sessionManager);
        this.remotePackageRepository = new RemotePackageRepository();
        this.packageMemoryCache = new PackageMemoryCache(this.remotePackageRepository);
    }
    /**
     * Initializes the client instance. This will start the client to begin listening for triggers and executing worklows.
     *
     * @public
     */
    Samelogic.prototype.init = function () {
        return __awaiter(this, void 0, void 0, function () {
            var project, _i, _a, workflow, workflowMetadata, context, eventLogger, manager;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!this.options.workflows) return [3 /*break*/, 1];
                        project = {
                            envId: this.options.envId,
                            workflows: this.options.workflows
                        };
                        return [3 /*break*/, 3];
                    case 1: return [4 /*yield*/, this.projectsRepository.getClientProjectByEnvironmentId(this.options.envId)];
                    case 2:
                        project = _b.sent();
                        _b.label = 3;
                    case 3:
                        if (project.workflows.length < 1) {
                            this.logger.info('No workflows loaded');
                        }
                        this.initSession(project);
                        return [4 /*yield*/, this.hotLoadPackages(project.workflows)];
                    case 4:
                        _b.sent();
                        for (_i = 0, _a = project.workflows; _i < _a.length; _i++) {
                            workflow = _a[_i];
                            workflowMetadata = {
                                name: workflow.name
                            };
                            context = new Context(this.sessionManager, workflowMetadata, {});
                            eventLogger = new EventLogger(workflow.logEndpoint);
                            eventLogger.setProject({
                                envId: project.envId,
                                projectId: project.projectId,
                                orgId: project.orgId
                            });
                            manager = new WorkflowManager(context, workflow, this.packageMemoryCache, this.logger, eventLogger);
                            this.workflowManagers.set(workflow.name, manager);
                            manager.start();
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    Samelogic.prototype.initSession = function (project) {
        this.sessionManager.setSessionProperties({
            envId: project.envId,
            projId: project.projectId || '',
            orgId: project.orgId || ''
        });
    };
    Samelogic.prototype.hotLoadPackages = function (workflows) {
        return __awaiter(this, void 0, void 0, function () {
            var stepUrls, _i, workflows_1, workflow, _a, _b, step;
            return __generator(this, function (_c) {
                stepUrls = [];
                for (_i = 0, workflows_1 = workflows; _i < workflows_1.length; _i++) {
                    workflow = workflows_1[_i];
                    for (_a = 0, _b = workflow.steps; _a < _b.length; _a++) {
                        step = _b[_a];
                        stepUrls.push(step.uses);
                    }
                }
                return [2 /*return*/, this.packageMemoryCache.initialize(stepUrls)];
            });
        });
    };
    /**
     * Creates an instance of the client and then initializes it. This is a static method and is a convenience method
     * for initializing through a single API call from the window object. This allows tools like Google Tag Manager
     * to initialize the client instance without the `new` keyword being available.
     *
     * @public
     */
    Samelogic.init = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var samelogic;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        samelogic = new Samelogic(options);
                        return [4 /*yield*/, samelogic.init()];
                    case 1:
                        _a.sent();
                        window.samelogic = samelogic;
                        return [2 /*return*/, samelogic];
                }
            });
        });
    };
    /**
     * This method is used to uniquelly identify the user for maintaining an association across sessions.
     *
     * @param identity - this is a unique identifier for the user, such as an email address or a user ID.
     * @param properties - this is a set of key/value pairs that can be used to enrich the user's profile.
     */
    Samelogic.prototype.identify = function (identity, properties) {
        var sessionIdentity = this.sessionManager.getSessionIdentity();
        sessionIdentity.identity = identity;
        sessionIdentity.properties = properties;
        this.sessionManager.setSessionIdentity(sessionIdentity);
        this.workflowManagers.forEach(function (x) { return x.eventLogger.setSessionIdentity(sessionIdentity); });
    };
    /**
     * This method is used to reset the user session and identity. This can be used when the user logs out.
     */
    Samelogic.prototype.resetIdentity = function () {
        var _this = this;
        this.sessionManager.resetSessionIdentity();
        this.workflowManagers.forEach(function (x) {
            return x.eventLogger.setSessionIdentity(_this.sessionManager.getSessionIdentity());
        });
    };
    return Samelogic;
}());
export { Samelogic };
//# sourceMappingURL=Samelogic.js.map